import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "@/app/hooks/useRouter";
import { useDealProcessContext } from "@/app/context/DealProcessProvider";
import { BID } from "@/app/utils/Constants";
import { useAuth } from "@/app/context/AuthContext";
import { useCoinsContext } from "@/app/context/MarketContext";
import { useWallet } from "@/app/context/WalletContext";
import useRoundDown from "@/app/utils/formatAmount";
import { COMPARISON_QUERY_KEYS, COMPARISON_QUERY_OPTIONS } from "@/app/apiServices/comparison/constants";
import { useUserScopedQueryKey } from "@/app/apiServices/queryScope";
import type { CoinInterface } from "@/app/interfaces/coin.interface";
import { getComparisonCommit, getComparisonAltPrice, getComparisonExchanges, getComparisonHistory, getComparisonSideReferencePrice, postComparisonOrder } from "@/app/apiServices/comparison/apiCalls";
import type { ComparisonCommitParams, ComparisonExchangesParams, ComparisonHistoryParams, ComparisonSideReferenceParams } from "@/app/components/Comparison/types/comparison.interface";
import type { ExchangeInterface } from "@/app/components/Comparison/types/exchangeTable.interface";
import type { ComparisonHistoryResponse, MinMaxState } from "@/app/components/Comparison/types/comparison.interface";
import getExchangeName from "@/app/utils/getExchangeName";
import { getInitialLang } from "@/app/utils/locale";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
type ComparisonExchangeQueryKind = "offers" | "quote";
export const useComparisonExchanges = (params, t0, t1, t2) => {
  const $ = _c(11);
  const enabled = t0 === undefined ? true : t0;
  const preservePreviousData = t1 === undefined ? false : t1;
  const kind = t2 === undefined ? "offers" : t2;
  let t3;
  if ($[0] !== kind || $[1] !== params) {
    t3 = params ? kind === "quote" ? COMPARISON_QUERY_KEYS.quote(params) : COMPARISON_QUERY_KEYS.exchanges(params) : kind === "quote" ? COMPARISON_QUERY_KEYS.quotesRoot : COMPARISON_QUERY_KEYS.exchangesRoot;
    $[0] = kind;
    $[1] = params;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== params) {
    t4 = t5 => {
      const {
        signal
      } = t5;
      return getComparisonExchanges(params as ComparisonExchangesParams, signal);
    };
    $[3] = params;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = !!params && enabled;
  const t6 = preservePreviousData ? _temp2 : undefined;
  const t7 = kind === "quote";
  let t8;
  if ($[5] !== t3 || $[6] !== t4 || $[7] !== t5 || $[8] !== t6 || $[9] !== t7) {
    t8 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      select: _temp,
      placeholderData: t6,
      ...COMPARISON_QUERY_OPTIONS,
      refetchOnWindowFocus: t7
    };
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  return useQuery(t8);
};
export const useComparisonSideReferencePrice = (params, t0, t1) => {
  const $ = _c(9);
  const enabled = t0 === undefined ? true : t0;
  const refetchInterval = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== params) {
    t2 = params ? COMPARISON_QUERY_KEYS.sideReference(params) : COMPARISON_QUERY_KEYS.root;
    $[0] = params;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== params) {
    t3 = t4 => {
      const {
        signal
      } = t4;
      return getComparisonSideReferencePrice(params as ComparisonSideReferenceParams, signal);
    };
    $[2] = params;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = !!params && enabled;
  let t5;
  if ($[4] !== refetchInterval || $[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      select: _temp3,
      refetchInterval,
      ...COMPARISON_QUERY_OPTIONS
    };
    $[4] = refetchInterval;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return useQuery(t5);
};
export const startCommitTracking = (run: () => Promise<unknown>, onError: (error: unknown) => void): Promise<void> => run().then(() => undefined, error => {
  onError(error);
});
export const createCommitTrackingGuard = () => {
  let isPending = false;
  return {
    get isPending() {
      return isPending;
    },
    async run(run: () => Promise<unknown>, onError: (error: unknown) => void) {
      if (isPending) return false;
      isPending = true;
      try {
        await startCommitTracking(run, onError);
        return true;
      } finally {
        isPending = false;
      }
    }
  };
};
export const useComparisonCommit = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: COMPARISON_QUERY_KEYS.commit(),
      mutationFn: getComparisonCommit
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useComparisonAltPrice = symbol => {
  const $ = _c(10);
  let normalizedSymbol;
  let t0;
  let t1;
  if ($[0] !== symbol) {
    normalizedSymbol = symbol?.toUpperCase();
    t1 = useQuery;
    t0 = COMPARISON_QUERY_KEYS.altPrice(normalizedSymbol);
    $[0] = symbol;
    $[1] = normalizedSymbol;
    $[2] = t0;
    $[3] = t1;
  } else {
    normalizedSymbol = $[1];
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== normalizedSymbol) {
    t2 = () => getComparisonAltPrice(normalizedSymbol as string);
    $[4] = normalizedSymbol;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = Boolean(normalizedSymbol);
  let t4;
  if ($[6] !== t0 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t2,
      enabled: t3,
      select: _temp4,
      ...COMPARISON_QUERY_OPTIONS
    };
    $[6] = t0;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t1(t4);
};
export const useComparisonHistory = (params, t0) => {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  const {
    token,
    user
  } = useAuth();
  let t1;
  if ($[0] !== params) {
    t1 = params ? COMPARISON_QUERY_KEYS.history(params) : COMPARISON_QUERY_KEYS.historyRoot;
    $[0] = params;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryKey = useUserScopedQueryKey(t1);
  let t2;
  if ($[2] !== params) {
    t2 = () => getComparisonHistory(params as ComparisonHistoryParams);
    $[2] = params;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !!params && enabled && !!token && !!user;
  let t4;
  if ($[4] !== queryKey || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey,
      queryFn: t2,
      enabled: t3,
      select: _temp5,
      ...COMPARISON_QUERY_OPTIONS
    };
    $[4] = queryKey;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useComparisonOrder = () => {
  const $ = _c(3);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = COMPARISON_QUERY_KEYS.order();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== queryClient) {
    t1 = {
      mutationKey: t0,
      mutationFn: postComparisonOrder,
      onSuccess: () => Promise.all([queryClient.invalidateQueries({
        queryKey: COMPARISON_QUERY_KEYS.historyRoot
      })])
    };
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useComparisonExchangeNames = items => {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getInitialLang() || "fa";
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const lang = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [exchangeNames, setExchangeNames] = useState(t1);
  let t2;
  if ($[2] !== items) {
    const ids = items.map(_temp6).filter(_temp7);
    t2 = Array.from(new Set(ids));
    $[2] = items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const exchangeIds = t2;
  let t3;
  let t4;
  if ($[4] !== exchangeIds) {
    t3 = () => {
      let isActive = true;
      if (!exchangeIds.length) {
        setExchangeNames({});
        return () => {
          isActive = false;
        };
      }
      const fetchExchangeNames = async () => {
        const entries = await Promise.all(exchangeIds.map(async exchange_0 => [exchange_0, await getExchangeName(exchange_0, lang)] as const));
        if (!isActive) {
          return;
        }
        setExchangeNames(Object.fromEntries(entries));
      };
      fetchExchangeNames();
      return () => {
        isActive = false;
      };
    };
    t4 = [exchangeIds, lang];
    $[4] = exchangeIds;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  return exchangeNames;
};
type UseComparisonFlowOptions = {
  syncUrl?: boolean;
  includeHistory?: boolean;
};
export const shouldSyncSideToUrl = ({
  syncUrl,
  isNavigatingAway,
  side,
  currentSideParam
}: {
  syncUrl: boolean;
  isNavigatingAway: boolean;
  side: string | null | undefined;
  currentSideParam: string | undefined;
}): boolean => {
  if (!syncUrl || isNavigatingAway) return false;
  return Boolean(side) && currentSideParam !== side;
};
export type ComparisonTradePair = {
  paymentAsset: string;
  receiveAsset: string;
  paymentAmount: number | null;
  estimatedReceiveAmount: number | null;
  quotePrice: number | null;
};
export const useComparisonFlow = ({
  syncUrl = true,
  includeHistory = true
}: UseComparisonFlowOptions = {}) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const {
    user
  } = useAuth();
  const {
    coins,
    markets
  } = useCoinsContext();
  const {
    wallet,
    walletAmounts,
    loading: isWalletLoading
  } = useWallet();
  const {
    side,
    setSide
  } = useDealProcessContext();
  const {
    roundDownNoFormat
  } = useRoundDown();
  const isFirstMount = useRef(true);
  const isNavigatingAwayRef = useRef(false);
  const isMountedRef = useRef(false);
  const commitTrackingGuardRef = useRef<ReturnType<typeof createCommitTrackingGuard> | null>(null);
  if (!commitTrackingGuardRef.current) {
    commitTrackingGuardRef.current = createCommitTrackingGuard();
  }
  const [amountValue, setAmountValue] = useState<number | null>(0);
  const [selectedExchange, setSelectedExchange] = useState<ExchangeInterface | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isExchangeTableVisible, setIsExchangeTableVisible] = useState<boolean>(false);
  const [market, setMarket] = useState<string>("");
  const [exchangesList, setExchangesList] = useState<ExchangeInterface[] | []>([]);
  const [isBillOpen, setIsBillOpen] = useState<boolean>(false);
  const [minMaxState, setMinMaxState] = useState<MinMaxState | null>(null);
  const [order, setOrder] = useState<Record<string, unknown> | null>(null);
  const [isIncreaseModalOpen, setIsIncreaseModalOpen] = useState<boolean>(false);
  const [isLowBalanceModalOpen, setIsLowBalanceModalOpen] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [history, setHistory] = useState<ComparisonHistoryResponse>({
    items: []
  });
  const [refreshAt, setRefreshAt] = useState<number | null>(null);
  const [selectedCoin, setSelectedCoin] = useState<CoinInterface | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<"IRT" | "USDT">("IRT");
  const [exchangeParams, setExchangeParams] = useState<ComparisonExchangesParams | null>(null);
  const [orderCommit, setOrderCommit] = useState<ExchangeInterface | null>(null);
  const [commitError, setCommitError] = useState<unknown>(null);
  const [pendingExchangeId, setPendingExchangeId] = useState<number | null>(null);
  const [isSearchButtonLoading, setIsSearchButtonLoading] = useState(false);
  const limit = 5;
  const amountParam = syncUrl ? searchParams.get("amount") : null;
  const amountQuery = amountParam ?? (amountValue !== null && amountValue !== undefined ? amountValue.toString() : "");
  const sideQueryRaw = syncUrl ? searchParams.get("side")?.toUpperCase() : undefined;
  const sideQuery = sideQueryRaw === "BUY" || sideQueryRaw === "SELL" ? sideQueryRaw : undefined;
  const effectiveSide = sideQuery ?? side;
  const hasAppliedSideFromUrl = useRef(false);
  const marketParams = syncUrl ? searchParams.get("market") : null;
  const marketQuery = marketParams ? `${marketParams}-${selectedCurrency}` : `${selectedCoin?.symbol}-${selectedCurrency}`;
  const maxOrderMultiplier = Number(user?.max_order_multiplier ?? 1);
  const effectiveMinMaxState = useMemo(() => {
    if (!minMaxState) return null;
    return {
      ...minMaxState,
      max_currency: minMaxState.max_currency! * maxOrderMultiplier,
      max_symbol: minMaxState.max_symbol! * maxOrderMultiplier
    };
  }, [maxOrderMultiplier, minMaxState]);
  const quoteAmount = useMemo(() => {
    const normalizedAmount = Number(amountQuery);
    return Number.isFinite(normalizedAmount) && normalizedAmount > 0 ? amountQuery : undefined;
  }, [amountQuery]);
  const debouncedQuoteAmount = useDebouncedValue(quoteAmount, 250);
  const quoteParams = useMemo<ComparisonExchangesParams | undefined>(() => {
    if (!(selectedCoin?.symbol && debouncedQuoteAmount && Number(debouncedQuoteAmount) > 0)) return undefined;
    return {
      market: `${selectedCoin.symbol}-${selectedCurrency}`,
      side: effectiveSide,
      amount: debouncedQuoteAmount,
      source: "comparison"
    };
  }, [debouncedQuoteAmount, effectiveSide, selectedCoin?.symbol, selectedCurrency]);
  const quoteReferenceParams = useMemo<ComparisonSideReferenceParams | undefined>(() => {
    if (!selectedCoin?.symbol || quoteParams) return undefined;
    return {
      market: `${selectedCoin.symbol}-${selectedCurrency}`,
      side: effectiveSide,
      source: "comparison"
    };
  }, [effectiveSide, quoteParams, selectedCoin?.symbol, selectedCurrency]);
  const usdtIrtQuoteParams = useMemo<ComparisonSideReferenceParams>(() => ({
    market: "USDT-IRT",
    side: effectiveSide,
    source: "comparison"
  }), [effectiveSide]);
  const irtMarkets = useMemo(() => {
    if (!markets?.length) return [];
    return markets.filter((item: CoinInterface & {
      name?: string;
    }) => item.symbol !== "IRT" && (item.name?.includes("-IRT") || item.name?.includes("IRT-")));
  }, [markets]);
  const usdtMarkets = useMemo(() => {
    if (!markets?.length) return [];
    return markets.filter((item_0: CoinInterface & {
      name?: string;
    }) => item_0.symbol !== "USDT" && item_0.symbol !== "IRT" && (item_0.name?.includes("-USDT") || item_0.name?.includes("USDT-")));
  }, [markets]);
  const irtCoin = useMemo(() => (coins as CoinInterface[] | undefined)?.find(c => c.symbol === "IRT") ?? null, [coins]);
  const usdtMarketsWithIrt = useMemo(() => irtCoin ? [irtCoin, ...usdtMarkets] : usdtMarkets, [irtCoin, usdtMarkets]);
  const currencyMarkets = selectedCurrency === "USDT" ? usdtMarketsWithIrt : irtMarkets;
  const [isHistoryReady, setIsHistoryReady] = useState(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => setIsHistoryReady(true), 2000);
    return () => clearTimeout(timer);
  }, []);
  const comparisonHistoryParams: ComparisonHistoryParams | null = includeHistory && user ? {
    limit,
    offset,
    source: "comparison"
  } : null;
  const {
    data: historyData,
    isLoading: isHistoryLoading
  } = useComparisonHistory(comparisonHistoryParams ?? undefined, !!comparisonHistoryParams && isHistoryReady);
  const {
    data: exchangesData = [],
    isFetching: isExchangesLoading,
    isError: isExchangesError,
    refetch: refetchExchanges,
    isSuccess: isExchangesSuccess
  } = useComparisonExchanges(exchangeParams ?? undefined, !!exchangeParams);
  const {
    data: quoteData = [],
    isFetching: isQuotePriceLoading
  } = useComparisonExchanges(quoteParams, !!quoteParams, true, "quote");
  const {
    data: quoteReferenceData,
    isFetching: isQuoteReferenceLoading
  } = useComparisonSideReferencePrice(quoteReferenceParams, !!quoteReferenceParams);
  const {
    data: usdtIrtReference
  } = useComparisonSideReferencePrice(usdtIrtQuoteParams, selectedCurrency === "IRT");
  const quotePrice = Number((quoteParams ? quoteData[0]?.proposed_price : quoteReferenceData?.price) || 0);
  const usdtIrtRate = Number(usdtIrtReference?.price || 0);
  const normalizedAmount_0 = Number(amountValue ?? 0);
  const hasValidQuote = !!selectedCoin?.symbol && Number.isFinite(normalizedAmount_0) && normalizedAmount_0 > 0 && !isQuotePriceLoading && !isQuoteReferenceLoading && Number.isFinite(quotePrice) && quotePrice > 0;
  const tradePair = useMemo<ComparisonTradePair | null>(() => {
    if (!selectedCoin?.symbol) return null;
    const isBuy = effectiveSide === BID;
    const paymentAsset = isBuy ? selectedCurrency : selectedCoin.symbol;
    const receiveAsset = isBuy ? selectedCoin.symbol : selectedCurrency;
    const paymentAmount = Number.isFinite(normalizedAmount_0) && normalizedAmount_0 > 0 ? normalizedAmount_0 : null;
    const estimatedReceiveAmount = paymentAmount && quotePrice > 0 ? isBuy ? paymentAmount / quotePrice : paymentAmount * quotePrice : null;
    return {
      paymentAsset,
      receiveAsset,
      paymentAmount,
      estimatedReceiveAmount,
      quotePrice: quotePrice > 0 ? quotePrice : null
    };
  }, [effectiveSide, normalizedAmount_0, quotePrice, selectedCoin?.symbol, selectedCurrency]);
  const {
    mutateAsync: fetchCommit
  } = useComparisonCommit();
  const clearSearchState = () => {
    setSelectedExchange(null);
    setExchangeParams(null);
    setExchangesList([]);
    setOrderCommit(null);
    setCommitError(null);
    setIsConfirmModalOpen(false);
    setIsExchangeTableVisible(false);
    setIsSearchButtonLoading(false);
    setRefreshAt(null);
  };
  const resetTradeForm = () => {
    const defaultCoin = irtMarkets.find((item_1: CoinInterface) => item_1.symbol === "BTC") ?? null;
    setSide(BID);
    setSelectedCurrency("IRT");
    setAmountValue(0);
    setSelectedCoin(defaultCoin);
    setMinMaxState(defaultCoin);
    setSelectedExchange(null);
    setExchangeParams(null);
    setExchangesList([]);
    setOrderCommit(null);
    setIsExchangeTableVisible(false);
    setRefreshAt(null);
  };
  useLayoutEffect(() => {
    setAmountValue(0);
    setIsModalOpen(false);
    setIsBillOpen(false);
    setIsConfirmModalOpen(false);
    setIsExchangeTableVisible(false);
    setRefreshAt(null);
  }, []);
  useEffect(() => {
    setHistory(historyData ?? {
      items: []
    });
  }, [historyData]);
  useEffect(() => {
    if (!irtMarkets.length) return;
    if (marketParams) {
      const coin = irtMarkets.find((item_2: CoinInterface) => item_2.symbol === marketParams);
      if (coin) {
        setSelectedCoin(coin);
        setMinMaxState(coin);
        isFirstMount.current = false;
        return;
      }
    }
    if (isFirstMount.current) {
      const btcIrt = irtMarkets.find((item_3: CoinInterface) => item_3.symbol === "BTC");
      if (btcIrt) {
        setSelectedCoin(btcIrt);
        setMinMaxState(btcIrt);
      }
      isFirstMount.current = false;
    }
  }, [irtMarkets, marketParams]);
  useEffect(() => {
    if (currencyMarkets.length && selectedCoin?.symbol) {
      const matchingCoin = currencyMarkets.find((item_4: CoinInterface) => item_4.symbol === selectedCoin.symbol);
      if (matchingCoin) {
        setMinMaxState({
          ...matchingCoin
        });
      }
    }
  }, [currencyMarkets, selectedCoin, side]);
  useEffect(() => {
    if (!syncUrl) {
      setSide("BUY");
      hasAppliedSideFromUrl.current = true;
      return;
    }
    if (hasAppliedSideFromUrl.current) return;
    if (sideQuery === "BUY") {
      setSide("BUY");
      hasAppliedSideFromUrl.current = true;
    } else if (sideQuery === "SELL") {
      setSide("SELL");
      hasAppliedSideFromUrl.current = true;
    } else {
      setSide("BUY");
      hasAppliedSideFromUrl.current = true;
    }
  }, [sideQuery, setSide, syncUrl]);
  useEffect(() => {
    const current = searchParams.get("side")?.toUpperCase();
    if (!shouldSyncSideToUrl({
      syncUrl,
      isNavigatingAway: isNavigatingAwayRef.current,
      side,
      currentSideParam: current
    })) return;
    const params = new URLSearchParams(searchParams.toString());
    params.set("side", side);
    router.replace(`${pathname}?${params.toString()}`);
  }, [pathname, router, searchParams, side, syncUrl]);
  useEffect(() => {
    if (amountQuery && !isNaN(Number(amountQuery))) {
      setAmountValue(Number(amountQuery));
    }
  }, [amountQuery]);
  useEffect(() => {
    if (isExchangesSuccess) {
      if (selectedExchange) {
        const newExchange = exchangesData.filter((exchange: ExchangeInterface) => exchange.exchange == selectedExchange?.exchange);
        setSelectedExchange(newExchange[0]);
      }
      setExchangesList(exchangesData);
      setIsExchangeTableVisible(true);
    }
  }, [isExchangesSuccess, exchangesData]);
  useEffect(() => {
    if (!isExchangesError) return;
    setExchangesList([]);
    setIsExchangeTableVisible(true);
  }, [isExchangesError]);
  useEffect(() => {
    if (!isExchangesLoading && isSearchButtonLoading) {
      setIsSearchButtonLoading(false);
    }
  }, [isExchangesLoading, isSearchButtonLoading]);
  useEffect(() => {
    if (!exchangeParams || refreshAt === null) return;
    const refreshIfDue = () => {
      if (Date.now() < refreshAt) return;
      void refetchExchanges();
      setRefreshAt(Date.now() + 30000);
    };
    const timeout = setTimeout(refreshIfDue, Math.max(0, refreshAt - Date.now()));
    document.addEventListener("visibilitychange", refreshIfDue);
    return () => {
      clearTimeout(timeout);
      document.removeEventListener("visibilitychange", refreshIfDue);
    };
  }, [exchangeParams, refreshAt, refetchExchanges]);
  useEffect(() => {
    setAmountValue(prev => prev === undefined ? 0 : prev);
  }, [amountValue, selectedCoin]);
  useEffect(() => {
    return () => {
      isFirstMount.current = true;
      setSide("BUY");
      setAmountValue(0);
      setSelectedCoin(null);
      setSelectedExchange(null);
      setIsModalOpen(false);
      setIsBillOpen(false);
      setIsConfirmModalOpen(false);
      setIsExchangeTableVisible(false);
    };
  }, []);
  const isMinMaxAmount = useMemo(() => {
    if (!selectedCoin || !effectiveMinMaxState) return true;
    const normalizedAmount_1 = amountValue ?? 0;
    if (side === BID) {
      const minCurrency = effectiveMinMaxState?.min_currency ?? 0;
      const maxCurrency = effectiveMinMaxState?.max_currency ?? Number.POSITIVE_INFINITY;
      return normalizedAmount_1 < minCurrency || normalizedAmount_1 > maxCurrency;
    }
    const minSymbol = effectiveMinMaxState.min_symbol ?? 0;
    const maxSymbol = effectiveMinMaxState.max_symbol ?? Number.POSITIVE_INFINITY;
    return normalizedAmount_1 < minSymbol || normalizedAmount_1 > maxSymbol;
  }, [amountValue, effectiveMinMaxState, selectedCoin, side]);
  const handleTabClick = (nextSide: "BUY" | "SELL") => {
    setSide(nextSide);
    setAmountValue(null);
    clearSearchState();
  };
  const handleAmount = (value: number) => {
    const nextAmount = side === BID ? roundDownNoFormat(value, selectedCurrency) : value;
    if (amountValue === nextAmount) return;
    clearSearchState();
    setAmountValue(nextAmount);
    if (amountValue === undefined && value === undefined) {
      // keep toast local in UI if needed
    }
  };
  const deleteQuery = () => {
    if (!syncUrl) return;
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.delete("market");
    newSearchParams.delete("amount");
    router.replace(`${pathname}?${newSearchParams.toString()}`);
  };
  const handleCurrencyChange = (currency: "IRT" | "USDT") => {
    const nextMarkets = currency === "IRT" ? irtMarkets : usdtMarkets;
    const matchingCoin_0 = nextMarkets.find((item_5: CoinInterface) => item_5.symbol === selectedCoin?.symbol);
    const fallbackCoin = nextMarkets.find((item_6: CoinInterface) => item_6.symbol === "BTC");
    const nextCoin = matchingCoin_0 ?? fallbackCoin ?? null;
    setSelectedCurrency(currency);
    setAmountValue(null);
    setSelectedCoin(nextCoin);
    setMinMaxState(nextCoin);
    clearSearchState();
  };
  const handleSelectedCoinChange = (coin_0: CoinInterface | null) => {
    setAmountValue(null);
    setSelectedCoin(coin_0);
    if (coin_0?.symbol === "USDT" && selectedCurrency === "USDT") {
      setSelectedCurrency("IRT");
    }
    clearSearchState();
  };
  const getExchanges = () => {
    const nextParams: ComparisonExchangesParams = {
      market: marketQuery,
      side: effectiveSide,
      amount: amountQuery,
      source: "comparison"
    };
    const shouldRefetch = exchangeParams && exchangeParams.market === nextParams.market && exchangeParams.side === nextParams.side && exchangeParams.amount === nextParams.amount && exchangeParams.source === nextParams.source;
    setExchangeParams(nextParams);
    if (shouldRefetch) {
      refetchExchanges();
    }
  };
  const getCommitTrackingId = async (exchange_0 = selectedExchange?.exchange) => {
    if (!exchange_0) return;
    const params_0: ComparisonCommitParams = {
      exchange: exchange_0,
      symbol: selectedCoin?.symbol ?? "USDT",
      currency: selectedCurrency,
      side: effectiveSide,
      amount: amountQuery,
      source: "comparison"
    };
    setOrderCommit(null);
    setCommitError(null);
    const data = await fetchCommit(params_0);
    if (isMountedRef.current) {
      setOrderCommit(data?.message ?? data);
    }
  };
  const handleExchange = async (exchange_1: ExchangeInterface | null) => {
    const commitTrackingGuard = commitTrackingGuardRef.current;
    if (!exchange_1 || !commitTrackingGuard || commitTrackingGuard.isPending) return;
    setSelectedExchange(exchange_1);
    setPendingExchangeId(exchange_1.id);
    try {
      await commitTrackingGuard.run(() => getCommitTrackingId(exchange_1.exchange), error => {
        if (isMountedRef.current) setCommitError(error);
      });
      if (isMountedRef.current) setIsConfirmModalOpen(true);
    } finally {
      if (isMountedRef.current) setPendingExchangeId(null);
    }
  };
  const runSearch = (showButtonLoading = false) => {
    if (isMinMaxAmount || !hasValidQuote) return false;
    setRefreshAt(Date.now() + 30000);
    if (showButtonLoading) {
      setIsSearchButtonLoading(true);
    }
    getExchanges();
    deleteQuery();
    return true;
  };
  const handleSearch = () => {
    runSearch(false);
  };
  const handleCtaSearch = () => {
    runSearch(true);
  };
  const handleOrderSuccess = (orderData: Record<string, unknown>) => {
    setIsBillOpen(true);
    setOrder(orderData);
  };
  const handleBillModalOpenChange = (open: boolean) => {
    setIsBillOpen(open);
    if (!open) {
      resetTradeForm();
    }
  };
  const setNavigatingAway = (value_0: boolean) => {
    isNavigatingAwayRef.current = value_0;
  };
  return {
    amountQuery,
    amountValue,
    commitError,
    exchangesList,
    getCommitTrackingId,
    getExchanges,
    handleAmount,
    handleExchange,
    handleOrderSuccess,
    handleBillModalOpenChange,
    setNavigatingAway,
    handleSearch,
    handleCtaSearch,
    handleTabClick,
    history,
    isBillOpen,
    isConfirmModalOpen,
    isDropdownOpen,
    isExchangeTableVisible,
    isSearchButtonLoading,
    isExchangesLoading,
    isQuotePriceLoading,
    isHistoryLoading,
    hasValidQuote,
    isIncreaseModalOpen,
    isLowBalanceModalOpen,
    isMinMaxAmount,
    isModalOpen,
    limit,
    market,
    minMaxState: effectiveMinMaxState,
    order,
    orderCommit,
    pendingExchangeId,
    offset,
    quotePrice,
    tradePair,
    selectedCoin,
    selectedCurrency,
    setSelectedCurrency,
    handleCurrencyChange,
    handleSelectedCoinChange,
    currencyMarkets,
    selectedExchange,
    setAmountValue,
    setIsBillOpen,
    setIsConfirmModalOpen,
    setIsDropdownOpen,
    setIsExchangeTableVisible,
    setIsIncreaseModalOpen,
    setIsLowBalanceModalOpen,
    setIsModalOpen,
    setMarket,
    setMinMaxState,
    setOffset,
    setSelectedCoin,
    setSelectedExchange,
    side,
    refreshAt,
    wallet,
    walletAmounts,
    isWalletLoading,
    coins,
    usdtIrtRate,
    user
  };
};
export type ComparisonFlowResult = ReturnType<typeof useComparisonFlow>;
function _temp(data) {
  return data?.message ?? [];
}
function _temp2(previousData) {
  return previousData;
}
function _temp3(data) {
  return data?.message;
}
function _temp4(data) {
  return data?.message ?? {};
}
function _temp5(data) {
  return data?.message ?? {
    items: []
  };
}
function _temp6(item) {
  return item?.exchange;
}
function _temp7(exchange) {
  return Boolean(exchange);
}